import { Separator } from '@components/ui/separator';

import type { VariantProps } from 'class-variance-authority';
import { ChevronDown, X } from 'lucide-react';
import type React from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { multiSelectVariants } from './multi-select';

interface BadgesProps extends VariantProps<typeof multiSelectVariants> {
  selectedOptions: {
    label: string;
    value: string;
  }[];
  maxItemCount?: number;
  maxItemDisplayableLength?: number;
  placeholder?: string;
  clickEvents?: {
    onItemCircleClick?: (
      event: React.MouseEvent,
      option: {
        label: string;
        value: string;
      }
    ) => void;
    onExtraOptionsCircleClick?: (event: React.MouseEvent) => void;
    onCrossClick?: (event: React.MouseEvent) => void;
  };
}

export const GroupedBadges = ({
  selectedOptions,
  clickEvents,
  maxItemCount = 1,
  maxItemDisplayableLength = 10,
  variant = 'pastelBlue',
  placeholder = 'Select options'
}: BadgesProps) =>
  selectedOptions.length > 0 ? (
    <div className="flex h-full w-full items-center justify-between gap-2">
      <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-hidden">
        {selectedOptions.slice(0, maxItemCount).map(option => (
          <Badge
            className={cn(multiSelectVariants({ variant }), 'shrink-0')}
            key={option.value}
          >
            {option.label.length > maxItemDisplayableLength
              ? option.label
                  .substring(0, maxItemDisplayableLength)
                  .concat('...')
              : option.label}
            {clickEvents?.onItemCircleClick && (
              <X
                className="ml-2 h-4 w-4 cursor-pointer"
                onClick={(event: React.MouseEvent) => {
                  clickEvents.onItemCircleClick?.(event, option);
                }}
              />
            )}
          </Badge>
        ))}
        {selectedOptions.length > maxItemCount && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  className={cn(multiSelectVariants({ variant }), 'shrink-0')}
                >
                  {`+ ${selectedOptions.length - maxItemCount} more`}
                  {clickEvents?.onExtraOptionsCircleClick && (
                    <X
                      className="ml-2 h-4 w-4 cursor-pointer"
                      onClick={clickEvents.onExtraOptionsCircleClick}
                    />
                  )}
                </Badge>
              </TooltipTrigger>
              <TooltipContent align="start" side="bottom">
                <div className="flex max-h-64 flex-col items-start gap-1 overflow-auto p-1">
                  {selectedOptions.slice(maxItemCount).map(option => (
                    <Badge
                      className={cn(multiSelectVariants({ variant }))}
                      key={option.value}
                    >
                      {option.label}
                    </Badge>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      {clickEvents?.onCrossClick && (
        <div className="flex items-center justify-between">
          <X
            className="mx-2 h-4 cursor-pointer text-muted-foreground"
            onClick={clickEvents.onCrossClick}
          />
          <Separator className="flex h-full min-h-6" orientation="vertical" />
          <ChevronDown className="mx-2 h-4 cursor-pointer text-muted-foreground" />
        </div>
      )}
    </div>
  ) : (
    <div className="mx-auto flex h-full w-full items-center justify-between">
      <span className="mx-3 text-muted-foreground text-sm">{placeholder}</span>
      {clickEvents && (
        <ChevronDown className="mx-2 h-4 cursor-pointer text-muted-foreground" />
      )}
    </div>
  );
